import Vue from 'vue';
import {indexOf, map, values} from 'lodash';
import {
    createAccessory,
    loadAccessoriesTypes,
    removeAccessory,
    updateAccessory,
    createAccessoryType,
    updateAccessoryType,
    removeAccessoryType
} from '../modules/api/accessory.api';

function initialState() {
    return {
        accessoryTypes: [],
    }
}

const getters = {
    accessoriesTypes:                (state) => state.accessoryTypes,
    accessoriesByOptionToBuy:        (state, getters) => (optionBuy) => {
        return getters.accessoriesTypes.filter(({ options }) => {
            const o = values(options);
            return o.filter(({ possibleToBuy }) => possibleToBuy === optionBuy).length > 0;
        }).map((accessory) => {
            const o = values(accessory.options);
            const options = o.filter(({ possibleToBuy }) => possibleToBuy === optionBuy);
            return { ...accessory, options }
        });
    },
    freeAccessories:                 (state, getters) => getters.accessoriesByOptionToBuy(false),
    accessoriesToBuy:                (state, getters) => getters.accessoriesByOptionToBuy(true),
    freeAccessoriesForSelect:        (state, getters) => getters.freeAccessories.map(({ title, id }) => {
        return { id, name: title };
    }),
    accessoriesToBuyForSelect:       (state, getters) => getters.accessoriesToBuy.map(({ title, id }) => {
        return { id, name: title };
    }),
    loadAccessoryTypeById:           (state, getters) => (accessoryTypeId) => {
        return new Promise((resolve) => resolve(getters.accessoriesTypes.find((accessory) => accessory.id === accessoryTypeId) || null));
    },
    accessoriesOwnOrFilled:          (state) => state.accessoryTypes.filter(({ systemic, options }) => {
        return false === systemic || options.length > 0;
    }),
    accessoriesTypesForAutocomplete: (state) => state.accessoryTypes.map(({ id, title }) => {
        return { name: title, id };
    }),
}

const actions = {
    async loadAccessoryTypes({ commit, rootGetters }) {
        const projectId = rootGetters['projects/project/projectId'];
        return loadAccessoriesTypes(projectId).then((data) => commit('SET_ACCESSORY_TYPES', data));
    },
    async createAccessoryType({ commit, rootGetters }, data) {
        const projectId = rootGetters['projects/project/projectId'];
        return createAccessoryType({ ...data, projectId }).then((data) => commit('ADD_ACCESSORY_TYPE', data))
    },
    async updateAccessoryType({ commit }, data) {
        return updateAccessoryType(data).then((data) => commit('UPDATE_ACCESSORY_TYPE', data))
    },
    async removeAccessoryType({ commit }, accessoryTypeId) {
        return removeAccessoryType(accessoryTypeId).then(() => commit('REMOVE_ACCESSORY_TYPE', accessoryTypeId))
    },

    async createAccessory({ commit, rootGetters }, data) {
        const projectId = rootGetters['projects/project/projectId'];
        const { accessoryTypeId } = data;
        return createAccessory({ ...data, projectId }).then((data) => commit('ADD_ACCESSORY', {
            ...data,
            accessoryTypeId
        }))
    },
    async updateAccessory({ commit }, data) {
        const { accessoryTypeId } = data;
        return updateAccessory(data).then((data) => commit('UPDATE_ACCESSORY', { ...data, accessoryTypeId }))
    },
    async removeAccessory({ commit }, ids) {
        const { id } = ids;
        return removeAccessory(id).then(() => commit('REMOVE_ACCESSORY', ids))
    },

    reset: ({ commit }) => commit('RESET'),
}

const mutations = {
    SET_ACCESSORY_TYPES:   (state, data) => state.accessoryTypes = data,
    ADD_ACCESSORY_TYPE:    (state, data) => state.accessoryTypes.push(data),
    UPDATE_ACCESSORY_TYPE: (state, data) => {
        const index = indexOf(map(state.accessoryTypes, (object) => object.id), data.id);
        Vue.set(state.accessoryTypes, index, data);
    },
    REMOVE_ACCESSORY_TYPE: (state, accessoryTypeId) => {
        const index = indexOf(map(state.accessoryTypes, (object) => object.id), accessoryTypeId);
        if (index !== -1) {
            state.accessoryTypes.splice(index, 1);
        }
    },

    ADD_ACCESSORY:    (state, data) => {
        const { accessoryTypeId } = data;
        const index = indexOf(map(state.accessoryTypes, (object) => object.id), accessoryTypeId);
        if (index !== -1) {
            const accessoryType = state.accessoryTypes[index];
            delete data.accessoryTypeId;
            accessoryType.options.push(data);
            Vue.set(state.accessoryTypes, index, accessoryType);
        }
    },
    UPDATE_ACCESSORY: (state, data) => {
        const { accessoryTypeId, id } = data;
        const index = indexOf(map(state.accessoryTypes, (object) => object.id), accessoryTypeId);
        if (index !== -1) {
            const accessoryType = state.accessoryTypes[index];
            const optionIndex = indexOf(map(accessoryType.options, (object) => object.id), id);
            if (optionIndex !== -1) {
                delete data.accessoryTypeId;
                Vue.set(state.accessoryTypes[index].options, optionIndex, data);
            }
        }
    },
    REMOVE_ACCESSORY: (state, ids) => {
        const { id, accessoryTypeId } = ids;
        const index = indexOf(map(state.accessoryTypes, (object) => object.id), accessoryTypeId);
        if (index !== -1) {
            const accessoryType = state.accessoryTypes[index];
            const roomIndex = indexOf(map(accessoryType.options, (object) => object.id), id);
            if (roomIndex !== -1) {
                accessoryType.options.splice(roomIndex, 1);
            }
        }
    },

    RESET: (state) => {
        const s = initialState();
        Object.keys(s).forEach(key => state[key] = s[key]);
    }
}

export default {
    namespaced: true,
    state:      initialState,
    getters,
    actions,
    mutations
}
